<template>
  <div class="shop" v-if="!loading">
    <slot name="shop-intro"></slot>

    <form @submit.prevent="handleShopFormSubmit">

      <div class="product">
        <div class="image">
          <PlaySVG />
        </div>
        <div class="content">
          <h3>{{ shopDigitalTitle }}: <Money :amount="digitalPrice" /></h3>
          <slot name="shop-digital-description"></slot>

          <div
            v-for="(digitalRecipient, index) in digitalRecipients"
            v-bind:key="index"
            class="digital-recipients form-row multi"
            :data-has-data="Boolean(digitalRecipient.email || digitalRecipient.name)"
          >
            <input
              placeholder="Email address"
              type="email"
              :required="Boolean(digitalRecipient.name)"
              :disabled="lockInputs"
              v-model="digitalRecipient.email"
              @keyup="handleDigitalRecipientEmailChange"
            />
            <input :disabled="lockInputs" placeholder="Name" type="text" v-model="digitalRecipient.name" />
            <button type="button" class="button-shop button-remove" :disabled="lockInputs" :data-index="index" @click.prevent="handleRemoveDigitalRecipientClick">Remove</button>
          </div>
        </div>
      </div>

      <div class="product">
        <div class="image">
          <PressSVG />
        </div>
        <div class="content">
          <h3>{{ shopPrintTitle }}: <Money :amount="printPrice" /></h3>
          <slot name="shop-print-description"></slot>

          <p><button type="button" class="button-shop" :disabled="lockInputs" v-if="printQuantity == 0" @click="handleStartPrintClick">Order print</button></p>

          <div class="print-details-container">
            <div v-if="printQuantity > 0" class="print-details">
              <p class="print-quantity-container">
                <span class="label">Number of prints:</span>
                <span class="controls">
                  <button type="button" @click.prevent="handlePrintQuantityDecreaseClick" class="number-control decrease">-</button><input class="print-quantity" :disabled="lockInputs" type="number" v-model="printQuantity" step="1" min="0" /><button type="button" @click.prevent="handlePrintQuantityIncreaseClick" class="number-control increase">+</button>
                </span>
              </p>

              <div class="shipping-details">
                <p>Deliver to:</p>

                <div class="form-group">
                  <p class="form-row"><input :disabled="lockInputs" type="text" required placeholder="Name" v-model="delivery.name" /></p>
                  <p class="form-row"><input :disabled="lockInputs" type="text" required placeholder="Address line 1" v-model="delivery.address_line_1" /></p>
                  <p class="form-row"><input :disabled="lockInputs" type="text" placeholder="Address line 2" v-model="delivery.address_line_2" /></p>
                  <p class="form-row"><input :disabled="lockInputs" type="text" required placeholder="City" v-model="delivery.address_city" /></p>
                  <p class="form-row"><input :disabled="lockInputs" type="text" required placeholder="Postcode" v-model="delivery.address_postcode" /></p>
                  <p class="form-row"><input disabled type="text" required placeholder="Country" v-model="delivery.address_country" /></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="cart" v-if="calcCartTotal > 0">
        <h3>Order summary</h3>
        <div class="cart-contents">
          <div class="cart-row" v-if="completeDigitalRecipients">
            <span class="qty">{{ completeDigitalRecipients.length }}</span>
            x
            <span class="product-name">Digital copies</span>
            @
            <span class="price"><Money :amount="digitalPrice" /></span>
          </div>

          <div class="cart-row" v-if="printQuantity > 0">
            <span class="qty">{{ printQuantity }}</span>
            x
            <span class="product-name">Prints</span>
            @
            <span class="price"><Money :amount="printPrice" /></span>
          </div>
          <div class="cart-row cart-total">Order total: <span class="price"><Money :amount=calcCartTotal /></span></div>

          <div class="cart-row">
            <button class="button-shop" type="submit" :disabled="lockInputs">Checkout</button>
          </div>
        </div>
      </div>
    </form>

    <div v-if="submittingOrder" class="submit-order-loader">
      <LoadingSpinner class="spinner-submit-order" />
    </div>

    <div class="checkout-container">
      <div class="checkout" v-if="checkout && !paymentSuccess">
        <form @submit.prevent="handlePaymentSubmit">
          <h3>Checkout</h3>

          <p><button class="button-shop" type="button" @click="handleCancelCheckoutClick">Cancel checkout</button></p>

          <slot name="shop-checkout-intro"></slot>

          <div class="form-group">
            <p class="form-row"><input type="text" :disabled="processingPayment" required placeholder="Your name" v-model="invoice.name" /></p>
            <p class="form-row"><input type="email" :disabled="processingPayment" required placeholder="Your email" v-model="invoice.email" /></p>

            <div class="payment-details">

              <div class="card-element" id="card-element" v-if="!paymentSuccess"></div>
              <div id="card-errors" role="alert" v-if="paymentError">
                <slot name="shop-checkout-error"></slot>
                <p>{{ paymentError }}</p>
              </div>

              <button class="button-shop" v-if="!processingPayment && !paymentSuccess" type="submit" :disabled="processingPayment">Pay <Money class="payment-amount" :amount="totalPrice" /></button>

              <LoadingSpinner class="spinner-processing-payment" v-if="processingPayment" />


            </div>
          </div>

        </form>

      </div>
      <div v-if="paymentSuccess" class="payment-success">
        <slot name="shop-checkout-success"></slot>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'

import Money from "@/components/Money.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

import PressSVG from "@/assets/press.svg"
import PlaySVG from "@/assets/play.svg"

export default {
  name: "Shop",
  components: {
    Money,
    LoadingSpinner,
    PressSVG,
    PlaySVG,
  },
  props: ["site", "shopDigitalTitle", "shopPrintTitle", "stripePublicKey"],
  data () {
    return {
      clientSecret: null,
      paymentIntentId: null,
      orderUUID: null,
      totalPrice: null,
      stripe: null,
      card: null,
      paymentSuccess: null,
      paymentError: null,
      loading: false,
      lockInputs: false,
      checkout: false,
      submittingOrder: false,
      processingPayment: false,
      digitalPrice: 10,
      printPrice: 50,
      digitalRecipients: [],
      printQuantity: 0,
      invoice: {
        name: "",
        email: "",
      },
      delivery: {
        name: "",
        address_line_1: "",
        address_line_2: "",
        address_city: "",
        address_postcode: "",
        address_country: "United Kingdom",
      },
    }
  },
  mounted () {
    this.addDigitalRecipient()

    this.stripe = window.Stripe(this.stripePublicKey);
    var elements = this.stripe.elements();
    var style = {
      base: {
        color: "#000000",
        fontSize: "16px",
      }
    };

    this.card = elements.create("card", { style: style });

    this.loading = true
    axios.get("/cms/api/v2/price/")
      .then((response) => {
        response.data.forEach((productPrice) => {
          if (productPrice.product === 1) {
            this.digitalPrice = parseFloat(productPrice.price)
          }
          if (productPrice.product === 2) {
            this.printPrice = parseFloat(productPrice.price)
          }
        })
        this.loading = false
      })
      .catch((err) => {
        console.log(err)
        this.loading = false
        this.error = err.response.status
      })
  },
  computed: {
    completeDigitalRecipients () {
      return this.digitalRecipients.filter((dr) => {
        return dr.email.trim()
      })
    },
    calcCartTotal () {
      let total = this.printPrice * this.printQuantity
      total += this.digitalPrice * this.completeDigitalRecipients.length
      return total
    },
  },
  methods: {
    handlePrintQuantityDecreaseClick () {
      this.printQuantity -= 1
      if (this.printQuantity < 0) {
        this.printQuantity = 0
      }
    },
    handlePrintQuantityIncreaseClick () {
      this.printQuantity += 1
    },
    handleRemoveDigitalRecipientClick (e) {
      const toRemove = parseInt(e.target.dataset.index)
      this.digitalRecipients = this.digitalRecipients.filter((value, index) => {
        return index !== toRemove
      })

      // Make sure there's always a form available
      this.addDigitalRecipient()
    },
    handleDigitalRecipientEmailChange (e) {
      if (e.target.value) {
        this.addDigitalRecipient()
      }
    },
    handleStartPrintClick () {
      this.printQuantity = 1
    },
    handleShopFormSubmit () {
      this.submittingOrder = true
      this.lockInputs = true
      this.submitOrder()
    },
    handleCancelCheckoutClick () {
      this.lockInputs = false
      this.checkout = false
    },
    handlePaymentSubmit (e) {
      if (!e.target.checkValidity || e.target.checkValidity()) {
        // stripe card element attempts to submit the form regardless of whether the
        // HTML5 validation has worked. so let's try and stop that.
        this.processingPayment = true
        this.stripe.confirmCardPayment(this.clientSecret, {
          payment_method: {
            card: this.card,
            billing_details: {
              name: this.invoice.name,
              email: this.invoice.email,
            }
          }
        })
          // stripe elements wrap any errors in the result - so need to pick errors in
          // .then, not .catch
          .then(this.handlePaymentConfirmation);
      }
    },
    handlePaymentConfirmation (result) {
      this.processingPayment = false
      if (result.error) {
        // Show error to your customer (e.g., insufficient funds)
        this.paymentError = result.error.message
        console.log(result.error.message);
      } else {
        // The payment has been processed!
        if (result.paymentIntent.status === 'succeeded') {
          const data = {
            contact_name: this.invoice.name,
            contact_email: this.invoice.email,
            payment_intent_id: this.paymentIntentId,
            amount_received: result.paymentIntent.amount,
          }
          axios.patch(`/cms/api/v2/order/${this.orderUUID}/`, data)
          .then((response) => {
            console.log(response)
            this.paymentSuccess = true
          })
          .catch((err) => {
            console.log(err)
          })
        }
      }
    },
    addDigitalRecipient () {
      const lastDR = this.digitalRecipients[this.digitalRecipients.length - 1]
      if (!lastDR || lastDR.email || lastDR.name) {
        this.digitalRecipients.push({email: "", name: ""})
      }
    },
    submitOrder () {
      this.submittingOrder = true

      const order = {
        site: this.site,

        shipping_name: this.delivery.name,
        shipping_address_line_1: this.delivery.address_line_1,
        shipping_address_line_2: this.delivery.address_line_2,
        shipping_address_city: this.delivery.address_city,
        shipping_address_postcode: this.delivery.address_postcode,
        shipping_address_country: this.delivery.address_country,

        digital_delivery_addresses: this.completeDigitalRecipients,

        order_lines: [
          {
            product: 1,
            quantity: this.completeDigitalRecipients.length,
          },
          {
            product: 2,
            quantity: this.printQuantity,
          }
        ]
      }

      axios.post("/cms/api/v2/order/", order)
      .then((response) => {
        console.log(response)
        this.clientSecret = response.data.client_secret
        this.paymentIntentId = response.data.payment_intent_id
        this.orderUUID = response.data.uuid
        this.totalPrice = parseFloat(response.data.total_price)

        this.submittingOrder = false
        this.checkout = true

        this.$nextTick()
          .then(() => {
            this.card.mount("#card-element");
          })
      })
      .catch((err) => {
        console.log(err)
      })
    },
  }
}
</script>

<style lang="scss" scoped>
  .shop {
    .button-shop {
      background: $color-blue;
      border: none;
      color: $color-white;
      padding: 0.75em 1.5em;
      border-radius: 100px;
      cursor: pointer;
      transition: background-color 0.1s ease-in-out;

      &:hover, &:active {
        background: $color-red;
      }

      &:disabled {
        background-color: $color-grey-black;
        cursor: default;
      }
    }

    .product {
      display: flex;
      flex-flow: row nowrap;
      justify-content: space-between;
      align-items: flex-start;
      padding: 25px 30px;

      .content {
        flex-grow: 1;
        width: 100%;
        h3 {
          margin-top: 0;
          padding-top: 0;
        }
      }

      .image {
        max-width: 120px;
        width: 50%;
        margin-right: 2em;

        svg {
          width: 100%;
        }

        @media screen and (max-width: $break-mobile) {
          display: none;
        }

      }
    }

    .digital-recipients {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      animation: slide-down 0.2s forwards;

      .button-remove {
        visibility: hidden;
      }

      &[data-has-data="true"] {
        .button-remove {
          visibility: visible;
        }
      }

      input {
        margin-right: 1em;
      }
    }

    .print-details-container {
      overflow: hidden;
    }
    .print-details {
      animation: muchos-slide-down 0.2s forwards;

      .number-control {
        padding: 0.5em 0.5em 0.5em 0.5em;
        font-size: 20px;
        text-align: center;
        background-color: $color-blue;
        border: 2px solid $color-blue;
        border-radius: 5px;
        color: $color-white;
        cursor: pointer;
      }

      .print-quantity-container {
        .label {
          padding-right: 1em;
        }

        @media screen and (max-width: $break-mobile) {
          display: flex;
          flex-flow: column nowrap;
          justify-items: flex-start;
          align-items: flex-start;

          .label {
            display: block;
            padding: 0 0 1em 0;
          }
        }

      }

      .print-quantity {
        width: 2em;
        padding: 0.5em 1em 0.5em 1em;
        font-size: 20px;
        border: none;
        border-top: solid 2px $color-grey;
        border-bottom: solid 2px $color-grey;
        box-shadow: none;
        outline: none;
        text-align: center;

        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: textfield;
        margin: 0;

        &::-webkit-inner-spin-button, &::-webkit-outer-spin-button {
            -webkit-appearance: none !important;
            margin: 0 !important;
        }

        &:disabled {
          color: $color-grey-black;
          background: transparent;
        }

        &:invalid {
          border:none;
          -moz-ui-invalid: none;
          box-shadow: none;
          border-bottom: solid 1px $color-red;
        }
      }

      .shipping-details {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;
        align-items: flex-start;

        @media screen and (max-width: $break-mobile) {
          flex-flow: column nowrap;
          justify-content: flex-start;
        }


        .form-group {
          padding-left: 1em;
          flex-grow: 1;

          @media screen and (max-width: $break-mobile) {
            padding-left: 0;
            width: 100%;
          }
        }
      }
    }

    .cart {
      display: flex;
      flex-flow: row nowrap;
      justify-content: space-between;
      align-items: flex-start;
      padding: 1em 2em;
      font-size: 0.8em;
      border-top: solid 3px $color-grey;
      border-bottom: solid 3px $color-grey;
      animation: muchos-slide-down 0.2s forwards;

      @media screen and (max-width: $break-mobile) {
        flex-flow: column nowrap;
        justify-content: flex-start;
        align-items: flex-end;
        padding: 1em 0;
      }


      h3 {
        margin: 0;
        padding: 0;
      }

      .cart-row {
        line-height: 2.5;
        text-align: right;

        &.cart-total {
          font-weight: bold;
        }
      }
    }

    .checkout-container {
      overflow:hidden;
    }

    .checkout {
      padding: 1em 2em;
      // font-size: 0.8em;
      border-bottom: solid 3px $color-grey;
      animation: muchos-slide-down 0.2s forwards;

      @media screen and (max-width: $break-mobile) {
        padding: 1em 0;
      }

      h3 {
        margin: 0;
        padding: 0;
      }
    }

    .payment-success {
      padding: 1em 2em;
      // font-size: 0.8em;
      border-bottom: solid 3px $color-grey;
      animation: muchos-slide-down 0.2s forwards;
    }

    .payment-details {
      padding: 1em;
      border: solid 3px $color-grey;
      position: relative;

      .spinner-processing-payment {
        position: absolute;
        top: calc(50% - 40px);
        right: 120px;
      }

      p {
        margin: 0 0 1em 0;
        padding: 0;
        font-size: 0.8em;
      }

      .button-shop {
        font-size: 0.8em;
      }

      .card-element {
        padding: 1em 0;
        margin-bottom: 1em;
        max-width: 500px;
        border: dotted 3px $color-red;

        @media screen and (max-width: $break-mobile) {
          max-width: none;
          border-left: none;
          border-right: none;
        }

      }
    }

    .submit-order-loader {
      padding: 1em 0;
      display: flex;
      flex-flow: row nowrap;
      justify-content: center;
    }

    @keyframes slide-down {
      0% {
        opacity: 0;
        margin-top: -2em;
      }
      100% {
        opacity: 1;
        margin-top: 0;
      }
    }

    @keyframes muchos-slide-down {
      0% {
        opacity: 0;
        margin-top: -10em;
      }
      100% {
        opacity: 1;
        margin-top: 0;
      }
    }

    .form-group {
      padding: 0.5em 0;

      &.small {
        font-size: 15px;
      }
    }

    .form-row {
      position: relative;
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      margin: 1em 0;

      @media screen and (max-width: $break-mobile) {
        &.multi {
          flex-flow: column nowrap;
          align-items: flex-start;
          padding-left: 1em;
          border-left: solid 3px $color-grey;

          > * {
            margin: 0.25em 0;
          }
        }
      }


      &:first-child {
        margin-top: 0;
      }

      input[type=text], input[type=email] {
        width: 100%;
        padding: 0.5em 0.1em;
        font-size: 20px;
        border:none;
        border-bottom: solid 1px $color-black;
        box-shadow: none;
        outline: none;

        &:disabled {
          color: $color-grey-black;
          background: transparent;
        }

        &:invalid {
          border:none;
          -moz-ui-invalid: none;
          box-shadow: none;
          border-bottom: solid 1px $color-red;
        }
      }

      input[type=checkbox] {
        opacity: 0;

        &:disabled {
          &, + label, + label a {
            color: $color-grey-black;
            background: transparent;
          }
          + label a {
            cursor: pointer;
          }
          &, + label {
            cursor: default;
          }
        }

        + label {
          cursor: pointer;
          padding-left: 0.5em;

          &:before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            display: block;
            height: calc(1em - 4px);
            width: calc(1em - 4px);
            padding: 3px;
            border-radius: 50%;
            border: solid 2px $color-red;
            background-color: $color-white;
            transition: background-color ease-in-out 0.2s;
          }

          a {
            text-decoration: underline;
          }
        }

        &:checked + label:before {
          background-clip: content-box;
          background-color: $color-red;
        }
      }
    }
  }
</style>
